// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import PhoneInput from "@/UI/inputs/PhoneInput";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyRestoreConflictDetails = {
    /** The trashed customer being restored. */
    id: string;
    /** The live one already holding the number, as the 409 named it. */
    customer: { _id: string; name: string; phone: string };
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * A customer cannot come back out of the trash under a phone somebody else now holds.
 *
 * That is not a validation error with a message, it is **two records for one human**, and the person
 * restoring is the only one who can say which. So the popup names the record already there and
 * offers the two real answers: go and look at it, or bring this one back under a corrected number.
 *
 * Changing the number rides on the restore itself rather than a customer edit route — the record is
 * still in the trash, and the trash screen has no business editing people.
 */
export default function RestoreConflictPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const navigate = useNavigate();
    const { id, customer, onDone } = details as TyRestoreConflictDetails;

    const [phone, setPhone] = useState<string | undefined>("");
    const [isChanging, setIsChanging] = useState(false);

    const { loading, error, succeededAt, invoke } = useFetch(`/trash/Customer/${id}/restore`, { method: "POST" }, false);

    useEffect(() => {
        if (!succeededAt) return;
        onClose();
        onDone();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <section className="restore-conflict">
            <header className="restore-conflict__head">
                <h2 className="restore-conflict__title">{t.crm.conflictTitle}</h2>
                <button type="button" className="restore-conflict__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <p className="restore-conflict__hint">{t.crm.conflictHint}</p>

            <div className="restore-conflict__who">
                <span className="restore-conflict__name">{customer.name}</span>
                <bdi className="restore-conflict__phone" dir="ltr">
                    {customer.phone}
                </bdi>
            </div>

            {isChanging && <PhoneInput value={phone} onChange={setPhone} placeholder={t.crm.newPhone} disabled={loading} />}

            {error && <p className="restore-conflict__error">{error}</p>}

            <footer className="restore-conflict__foot">
                <button
                    type="button"
                    className="restore-conflict__ghost"
                    onClick={() => {
                        onClose();
                        navigate(`/crm/customers/${customer._id}`);
                    }}
                >
                    {t.crm.viewCustomer}
                </button>

                <button
                    type="button"
                    className="restore-conflict__primary"
                    disabled={loading || (isChanging && !(phone ?? "").trim())}
                    onClick={() => {
                        if (!isChanging) return setIsChanging(true);
                        invoke({ method: "POST", data: { phone: (phone ?? "").replace(/\s/g, "") } });
                    }}
                >
                    {loading ? t.common.loading : t.crm.changePhone}
                </button>
            </footer>
        </section>
    );
}
